// Registre des visionneuses d'artefacts : type → {label, Icon, Component}.
// Ajouter un type (graphique au sprint 3, carte au sprint 4, documents au sprint 5)
// = l'enregistrer ici ; le panneau n'a rien à changer.
// Les visionneuses sont mémoïsées : le panneau se redessine à chaque rafale du
// streaming, mais une visionneuse ne se redessine que si son artefact change.
// Seules celles qui déclarent `needsMessages` reçoivent la liste des messages.
import { memo, Suspense } from 'react';
import { AgentIcon, BrainIcon, CalendarIcon, ChartIcon, CodeIcon, FileTextIcon, GlobeIcon, MapIcon, TableIcon } from '../icons';
import { VisualBoundary } from '../visuals/MessageVisuals';
import { retryableLazy } from '../visuals/retryableLazy.js';
import { CodeArtifact } from './CodeArtifact';
import { TableArtifact } from './TableArtifact';
import { FileArtifact } from './FileArtifact';
import { ReasoningArtifact } from './ReasoningArtifact';

// Visuel (sprint 3) : visionneuse chargée à la demande, comme ECharts ; un échec
// de chargement n'est pas définitif (retryableLazy)
const VisualPanel = retryableLazy(() => import('../visuals/VisualPanel.jsx'));
// Sprint 4 : carte (MapLibre), planning, page web — même principe
const MapPanel = retryableLazy(() => import('../visuals/map/MapPanel.jsx'));
// Carte live (03/11) : une seule carte pour la conversation, modifiée en direct
const LiveMapPanel = retryableLazy(() => import('../visuals/map/LiveMapPanel.jsx'));
const PlanningPanel = retryableLazy(() => import('../visuals/planning/PlanningPanel.jsx'));
const EmbedPanel = retryableLazy(() => import('../visuals/embed/EmbedPanel.jsx'));
// Sprint 5 : documents générés (aperçu PDF, mise en page Word, classeur) ;
// docx-preview et SheetJS ne sont téléchargés qu'à l'ouverture de leur onglet
const DocumentPanel = retryableLazy(() => import('../documents/DocumentPanel.jsx'));
// Agents (P1) : fiche d'un agent (payload {agentId, role?}), ouverte depuis le centre des agents
// ou une notification ; un rapport reçu s'ouvre, lui, comme un document
const AgentSheet = retryableLazy(() => import('../agents/AgentSheet.jsx'));

function lazyViewer(Panel, label) {
  function LazyViewer(props) {
    return (
      <VisualBoundary>
        <Suspense fallback={<div className="artifact-skeleton" aria-label={label} role="status" />}>
          <Panel {...props} />
        </Suspense>
      </VisualBoundary>
    );
  }
  return LazyViewer;
}
const VisualViewer = lazyViewer(VisualPanel, 'Chargement du visuel');

const registry = new Map([
  ['code', { label: 'Code', Icon: CodeIcon, Component: memo(CodeArtifact) }],
  ['table', { label: 'Tableau', Icon: TableIcon, Component: memo(TableArtifact) }],
  ['file', { label: 'Fichier', Icon: FileTextIcon, Component: memo(FileArtifact) }],
  ['visual', { label: 'Visuel', Icon: ChartIcon, Component: memo(VisualViewer) }],
  ['map', { label: 'Carte', Icon: MapIcon, Component: memo(lazyViewer(MapPanel, 'Chargement de la carte')) }],
  ['carte-live', { label: 'Carte live', Icon: MapIcon, Component: memo(lazyViewer(LiveMapPanel, 'Chargement de la carte live')) }],
  ['planning', { label: 'Planning', Icon: CalendarIcon, Component: memo(lazyViewer(PlanningPanel, 'Chargement du planning')) }],
  ['embed', { label: 'Page web', Icon: GlobeIcon, Component: memo(lazyViewer(EmbedPanel, 'Chargement de la page')) }],
  ['document', { label: 'Document', Icon: FileTextIcon, Component: memo(lazyViewer(DocumentPanel, 'Chargement du document')) }],
  ['reasoning', { label: 'Raisonnement', Icon: BrainIcon, Component: ReasoningArtifact, needsMessages: true }],
  ['agent', { label: 'Agent', Icon: AgentIcon, Component: memo(lazyViewer(AgentSheet, 'Chargement de la fiche de l’agent')) }],
]);

export function registerArtifactRenderer(type, definition) {
  registry.set(type, definition);
}

export function getArtifactRenderer(type) {
  return registry.get(type) ?? null;
}
